import type { BookingContent } from '@mawkaa/types';
import { Calendar, Clock, User } from 'lucide-react';

interface BookingBlockProps {
  content: BookingContent;
  isEditing?: boolean;
}

export function BookingBlock({ content, isEditing }: BookingBlockProps) {
  const { title, subtitle, services = [], showDate = true, showTime = true, showGuests = false } = content;

  return (
    <section className="px-4 py-16 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold">{title || 'Réserver'}</h2>
          {subtitle && <p className="mt-2 text-muted-foreground">{subtitle}</p>}
        </div>

        <form className="bg-white rounded-2xl shadow-sm p-8 space-y-6" data-booking-form>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Nom complet</label>
              <input
                type="text"
                name="name"
                placeholder="Votre nom"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Téléphone</label>
              <input
                type="tel"
                name="phone"
                placeholder="+216 XX XXX XXX"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              name="email"
              placeholder="votre@email.com"
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          {services.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">Service</label>
              <select
                name="service"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Sélectionner un service</option>
                {services.map((service, index) => (
                  <option key={index} value={service.name}>
                    {service.name} {service.duration && `(${service.duration})`} {service.price && `- ${service.price}`}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {showDate && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                />
              </div>
            )}
            {showTime && (
              <div>
                <label className="block text-sm font-medium mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Heure
                </label>
                <input
                  type="time"
                  name="time"
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                />
              </div>
            )}
          </div>

          {showGuests && (
            <div>
              <label className="block text-sm font-medium mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Nombre de personnes
              </label>
              <select
                name="guests"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'personne' : 'personnes'}</option>
                ))}
                <option value="more">Plus de 10</option>
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">Message (optionnel)</label>
            <textarea
              name="message"
              rows={3}
              placeholder="Informations supplémentaires..."
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none"
            />
          </div>

          <button
            type="submit"
            className="w-full py-4 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Confirmer la réservation
          </button>
        </form>
      </div>
    </section>
  );
}
